import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { motion } from 'motion/react';

export interface PopoverTraceBorderProps {
  active: boolean;
  color: string;
  duration?: number;
  offset?: number;
  overlap?: number;
  radius?: number;
  strokeWidth?: number;
}

export const POPOVER_TRACE_BORDER_DEFAULTS = {
  duration: 0.14,
  offset: 0,
  overlap: 12,
  radius: 8,
  strokeWidth: 3,
} as const;

export const PopoverTraceBorder = ({
  active,
  color,
  duration = POPOVER_TRACE_BORDER_DEFAULTS.duration,
  offset = POPOVER_TRACE_BORDER_DEFAULTS.offset,
  overlap: requestedOverlap = POPOVER_TRACE_BORDER_DEFAULTS.overlap,
  radius: requestedRadius = POPOVER_TRACE_BORDER_DEFAULTS.radius,
  strokeWidth = POPOVER_TRACE_BORDER_DEFAULTS.strokeWidth,
}: PopoverTraceBorderProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const updateSize = () => {
      const { width, height } = node.getBoundingClientRect();
      setSize(previous =>
        previous.width === width && previous.height === height ? previous : { width, height },
      );
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const width = Math.max(size.width, strokeWidth);
  const height = Math.max(size.height, strokeWidth);
  const halfStroke = strokeWidth / 2;
  const left = halfStroke;
  const top = halfStroke;
  const right = width - halfStroke;
  const bottom = height - halfStroke;
  const radius = Math.max(
    0,
    Math.min(requestedRadius - halfStroke, width / 2 - halfStroke, height / 2 - halfStroke),
  );
  const overlap = Math.min(requestedOverlap, radius);
  const topRightToBottomLeft = [
    `M ${right - radius - overlap} ${top}`,
    `Q ${right} ${top} ${right} ${top + radius}`,
    `L ${right} ${bottom - radius}`,
    `Q ${right} ${bottom} ${right - radius} ${bottom}`,
    `L ${left + radius - overlap} ${bottom}`,
  ].join(' ');
  const bottomLeftToTopRight = [
    `M ${left + radius + overlap} ${bottom}`,
    `Q ${left} ${bottom} ${left} ${bottom - radius}`,
    `L ${left} ${top + radius}`,
    `Q ${left} ${top} ${left + radius} ${top}`,
    `L ${right - radius + overlap} ${top}`,
  ].join(' ');

  return (
    <Box
      ref={ref}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: -offset,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {size.width > 0 && size.height > 0 && (
        <motion.svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          style={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}
        >
          {[topRightToBottomLeft, bottomLeftToTopRight].map(path => (
            <motion.path
              key={path}
              d={path}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              animate={{ pathLength: active ? 1 : 0, opacity: active ? 1 : 0 }}
              initial={false}
              transition={{ duration, ease: 'easeOut' }}
            />
          ))}
        </motion.svg>
      )}
    </Box>
  );
};
